import { useRef, useState } from "react";
import { parsePpeResponse, ppeAuthHeaders, ppePricingUrl } from "../../config";
import { formatPpeSearchParams } from "../../pricingRequest";
import { normalizePpeEngineResponse } from "../../resultData";

// The request body for one loan term of a search. The page prices each
// ticked term with its own request, so a term ticked later in the filter bar
// is priced alone (D26) and the others are not re-quoted. Work package 2's
// `loanTerms` would let one request carry several terms; the results already
// understand that shape (resultModel.splitResponseByTerm), but one request per
// term keeps "add a term" cheap and works with or without it.
export function pricingBodyForTerm(formBody, term) {
  return formatPpeSearchParams({ ...formBody, selectedLoanTerms: [term], loanTerm: term }).pricingRequest;
}

export function fetchTermQuote(formBody, term, fetchImpl = fetch) {
  return fetchImpl(ppePricingUrl(), {
    method: "POST",
    headers: ppeAuthHeaders(),
    body: JSON.stringify(pricingBodyForTerm(formBody, term)),
  })
    .then(parsePpeResponse)
    .then(normalizePpeEngineResponse);
}

// quotes[term] = { status: "loading" | "done" | "error", response?, error? }.
// `quote` starts a new search (a new searchKey; earlier answers still in
// flight are dropped); `addTerms` prices more terms into the current one.
export default function usePpeQuotes(fetchImpl) {
  const [state, setState] = useState({ searchKey: 0, quotes: {}, body: null });
  const generation = useRef(0);

  const run = (formBody, terms, fresh) => {
    if (fresh) generation.current += 1;
    const mine = generation.current;
    setState((prev) => ({
      searchKey: fresh ? mine : prev.searchKey,
      body: fresh ? formBody : prev.body,
      quotes: {
        ...(fresh ? {} : prev.quotes),
        ...Object.fromEntries(terms.map((term) => [term, { status: "loading" }])),
      },
    }));
    return Promise.all(terms.map((term) => fetchTermQuote(formBody, term, fetchImpl)
      .then(
        (response) => ({ status: "done", response }),
        (err) => ({ status: "error", error: (err && err.message) || "An internal error has occurred", title: err && err.title }),
      )
      .then((quote) => {
        if (mine === generation.current) {
          setState((prev) => ({ ...prev, quotes: { ...prev.quotes, [term]: quote } }));
        }
        return quote;
      })));
  };

  return {
    ...state,
    quote: (formBody, terms) => run(formBody, terms, true),
    addTerms: (formBody, terms) => run(formBody, terms, false),
  };
}
